  <template>
    <!-- Ne pas afficher les sections qui font partie d'un dropdown parent sauf si contexte récursif -->
    <div v-if="!shouldHideFromDropdownParent && shouldDisplaySection(compiledSectionData)" class="anamnesis-section"
      :class="{ 'border rounded-lg p-6 bg-white shadow-sm mb-6': level === 0 }">

      <!-- SECTION COLLAPSE -->
      <div v-if="sectionDisplayType === 'collapse'" class="collapse-section">
        <!-- En-tête avec bouton de collapse -->
        <div class="collapse-header" @click="toggleCollapse">
          <div class="flex items-center cursor-pointer hover:bg-gray-50 rounded-md p-2 transition-colors">
            <!-- Icône flèche -->
            <svg class="collapse-arrow w-4 h-4 text-gray-500 transition-transform duration-200"
              :class="{ 'rotate-90': !isCollapsed }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>

            <!-- Titre et tooltip collapse -->
            <div class="ml-2 flex-1">
              <div class="flex items-center">
                <h3 class="text-lg font-semibold">{{ sectionTitle }}</h3>
                <div v-if="sectionDescription" class="group relative inline-block ml-2 pb-1">
                  <InfoIcon class="w-4 h-4 text-gray-500 cursor-pointer" />
                  <div class="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                    {{ sectionDescription }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contenu collapsible -->
        <div class="collapse-content" :class="{ 'collapsed': isCollapsed }">
          <div class="collapse-content-inner pl-6 pt-2">
            <div v-if="childSections.length > 0" class="space-y-4">
              <div v-for="child in childSections" :key="child.section_id || child.id" class="child-section">
                <!-- Récursion pour enfants non-finaux -->
                <RecursiveSection v-if="!isFinalField(child) && shouldDisplaySection(child)"
                  :compiled-section-data="child" :data-entries="dataEntries" :level="level + 1"
                  :compiled-schema="compiledSchema" @update:data-entry="$emit('update:data-entry', $event)" />

                <!-- Champs finaux -->
                <div v-else-if="shouldDisplayField(child)" class="anamnesis-form-group">
                  <div v-if="child.type === 'text'" class="flex flex-col">
                    <label :for="child.id" class="anamnesis-form-label">{{ getFieldTitle(child) }}</label>
                    <input :id="child.id" type="text"
                      :placeholder="getFieldDescription(child) || 'Entrez une valeur'" :value="getFieldValue(child)"
                      class="anamnesis-form-input" @input="handleInputChange(child, $event)" />
                  </div>
                  <div v-else-if="child.type === 'textarea'" class="flex flex-col">
                    <label :for="child.id" class="anamnesis-form-label">{{ getFieldTitle(child) }}</label>
                    <textarea :id="child.id"
                      :placeholder="getFieldDescription(child) || 'Entrez votre texte'" :value="getFieldValue(child)"
                      rows="3" class="anamnesis-form-textarea" @input="handleInputChange(child, $event)"></textarea>
                  </div>
                  <div v-else-if="child.type === 'bool'">
                    <div class="flex items-center">
                      <label :for="child.id" class="anamnesis-form-label">{{ getFieldTitle(child) }}</label>
                      <div v-if="getFieldDescription(child)" class="group relative inline-block ml-2">
                        <InfoIcon class="w-4 h-4 text-gray-500 cursor-pointer" />
                        <div class="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                          {{ getFieldDescription(child) }}
                        </div>
                      </div>
                    </div>
                    <div class="flex space-x-2">
                      <button type="button"
                        :class="['py-2 px-4 rounded', getFieldValue(child)==='true' ? 'bg-blue-400 text-white' : 'bg-white text-gray-800 border']"
                        @click="handleBoolClick(child, 'true')">
                        Oui
                      </button>
                      <button type="button"
                        :class="['py-2 px-4 rounded', getFieldValue(child)==='false' ? 'bg-blue-400 text-white' : 'bg-white text-gray-800 border']"
                        @click="handleBoolClick(child, 'false')">
                        Non
                      </button>
                      <button type="button"
                        :class="['py-2 px-4 rounded', getFieldValue(child)==='' ? 'bg-blue-400 text-white' : 'bg-white text-gray-800 border']"
                        @click="handleBoolClick(child, '')">
                        Je ne sais pas
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION DROPDOWN -->
      <div v-else-if="sectionDisplayType === 'dropdown'" class="dropdown-section">
        <!-- En-tête de la section dropdown -->
        <div class="dropdown-header">
          <!-- Titre et tooltip dropdown -->
          <div class="flex items-center mb-2">
            <h3 class="text-lg font-semibold">{{ sectionTitle }}</h3>
            <div v-if="sectionDescription" class="group relative inline-block ml-2 pb-1">
              <InfoIcon class="w-4 h-4 text-gray-500 cursor-pointer" />
              <div class="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                {{ sectionDescription }}
              </div>
            </div>
          </div>
        </div>

        <!-- Liste déroulante des sous-sections disponibles -->
        <div v-if="availableDropdownChildren.length > 0" class="dropdown-selector mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Ajouter une section :</label>
          <select v-model="selectedDropdownChildId" class="anamnesis-form-input" @change="handleDropdownSelection">
            <option value="">-- Sélectionnez une section --</option>
            <option v-for="child in availableDropdownChildren" :key="child.section_id || child.id"
              :value="child.section_id || 0">
              {{ getFieldTitle(child) }}
            </option>
          </select>
        </div>

        <!-- Affichage des sous-sections sélectionnées -->
        <div v-if="selectedDropdownChildren.length > 0" class="selected-subsections space-y-4">
          <div v-for="selectedChild in selectedDropdownChildren" :key="selectedChild.section_id || selectedChild.id"
            class="selected-subsection-item bg-gray-50 border border-gray-200 rounded-lg p-4">
            <!-- Header avec bouton de suppression (sauf si rempli) -->
            <div class="flex justify-between items-start mb-3">
              <h4 class="font-medium text-gray-900">{{ getFieldTitle(selectedChild) }}</h4>
              <button v-if="!hasFilledChildFields(selectedChild)" class="text-red-500 hover:text-red-700 p-1"
                :aria-label="`Supprimer ${getFieldTitle(selectedChild)}`"
                @click="removeDropdownChild(selectedChild.section_id || 0)">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div v-else class="text-green-600 p-1" title="Section avec données saisies">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            <!-- Contenu de la sous-section -->
            <div class="subsection-content">
              <!-- Si c'est un champ final, l'afficher directement -->
              <div v-if="isFinalField(selectedChild) && shouldDisplayField(selectedChild)" class="anamnesis-form-group">
                <div v-if="selectedChild.type === 'text'" class="flex flex-col">
                  <label :for="selectedChild.id" class="anamnesis-form-label">{{ getFieldTitle(selectedChild) }}</label>
                  <input :id="selectedChild.id" type="text"
                    :placeholder="getFieldDescription(selectedChild) || 'Entrez une valeur'"
                    :value="getFieldValue(selectedChild)" class="anamnesis-form-input"
                    @input="handleInputChange(selectedChild, $event)" />
                </div>
                <div v-else-if="selectedChild.type === 'textarea'" class="flex flex-col">
                  <label :for="selectedChild.id" class="anamnesis-form-label">{{ getFieldTitle(selectedChild) }}</label>
                  <textarea :id="selectedChild.id"
                    :placeholder="getFieldDescription(selectedChild) || 'Entrez votre texte'"
                    :value="getFieldValue(selectedChild)" rows="3" class="anamnesis-form-textarea"
                    @input="handleInputChange(selectedChild, $event)"></textarea>
                </div>
                <div v-else-if="selectedChild.type === 'bool'" class="flex space-x-2">
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(selectedChild)==='true' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(selectedChild, 'true')">
                    Oui
                  </button>
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(selectedChild)==='false' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(selectedChild, 'false')">
                    Non
                  </button>
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(selectedChild)==='' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(selectedChild, '')">
                    Je ne sais pas
                  </button>
                </div>
              </div>
              <!-- Sinon, utiliser la récursion pour les sections complexes -->
              <RecursiveSection v-else-if="shouldDisplaySection(selectedChild)" :compiled-section-data="selectedChild"
                :data-entries="dataEntries" :level="level + 1" :compiled-schema="compiledSchema"
                @update:data-entry="$emit('update:data-entry', $event)" />
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION NORMALE (par défaut) -->
      <div v-else class="normal-section">
        <div class="mb-6">
          <!-- THIRD_EDIT: Titre et tooltip normal -->
          <div class="flex items-center">
            <h3 :class="level === 0 ? 'text-2xl font-semibold mb-4' : 'anamnesis-section-title'">{{ sectionTitle }}</h3>
            <div v-if="sectionDescription" class="group relative inline-block ml-2 mb-2">
              <InfoIcon class="w-4 h-4 text-gray-500 cursor-pointer" />
              <div class="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                {{ sectionDescription }}
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <div v-for="subSectionData in childSections" :key="subSectionData.section_id || subSectionData.id"
            :class="{ 'ml-4 pl-4 border-l': level > 0 }">
            <RecursiveSection v-if="!isFinalField(subSectionData) && shouldDisplaySection(subSectionData)"
              :compiled-section-data="subSectionData" :data-entries="dataEntries" :level="level + 1"
              :compiled-schema="compiledSchema" @update:data-entry="$emit('update:data-entry', $event)" />

            <div v-else-if="shouldDisplayField(subSectionData)" class="anamnesis-form-group">
              <div v-if="subSectionData.type === 'text'" class="flex flex-col">
                <label :for="subSectionData.id" class="anamnesis-form-label">{{ getFieldTitle(subSectionData) }}</label>
                <input :id="subSectionData.id" type="text"
                  :placeholder="getFieldDescription(subSectionData) || 'Entrez une valeur'"
                  :value="getFieldValue(subSectionData)" class="anamnesis-form-input"
                  @input="handleInputChange(subSectionData, $event)" />
              </div>
              <div v-else-if="subSectionData.type === 'textarea'" class="flex flex-col">
                <label :for="subSectionData.id" class="anamnesis-form-label">{{ getFieldTitle(subSectionData) }}</label>
                <textarea :id="subSectionData.id"
                  :placeholder="getFieldDescription(subSectionData) || 'Entrez votre texte'"
                  :value="getFieldValue(subSectionData)" rows="3" class="anamnesis-form-textarea"
                  @input="handleInputChange(subSectionData, $event)"></textarea>
              </div>
              <div v-else-if="subSectionData.type === 'bool'">
                <div class="flex items-center">
                  <label :for="subSectionData.id" class="anamnesis-form-label">{{ getFieldTitle(subSectionData) }}</label>
                  <div v-if="getFieldDescription(subSectionData)" class="group relative inline-block ml-2">
                    <InfoIcon class="w-4 h-4 text-gray-500 cursor-pointer" />
                    <div class="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-10">
                      {{ getFieldDescription(subSectionData) }}
                    </div>
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(subSectionData)==='true' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(subSectionData, 'true')">
                    Oui
                  </button>
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(subSectionData)==='false' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(subSectionData, 'false')">
                    Non
                  </button>
                  <button type="button"
                    :class="['py-2 px-4 rounded', getFieldValue(subSectionData)==='' ? 'bg-blue-600 text-white' : 'bg-white text-gray-800 border']"
                    @click="handleBoolClick(subSectionData, '')">
                    Je ne sais pas
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Affichage des champs externes (non présents dans le schéma courant) -->
          <!-- Affichage des branches orphelines alignées dans la bonne section -->
          <template v-if="compiledSectionData.id === 'WHICH' || compiledSectionData.id === 'HOW'">
            <div class="mt-4">
              <div v-if="orphanGraftsByAnchor[compiledSectionData.id] && orphanGraftsByAnchor[compiledSectionData.id].length" class="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon w-4 h-4 text-yellow-500 mr-1"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>
                <span class="text-sm text-yellow-700">Sections importées d'un modèle externe (non présentes dans ce schéma)</span>
              </div>
              <div>
                <template v-for="({ orphanPath: orphanPathOrphan, entry: entryOrphan }) in orphanGraftsByAnchor[compiledSectionData.id] || []" :key="entryOrphan.name">
                  <OrphanGraftRenderer :orphan-path="orphanPathOrphan" :entry="entryOrphan" :imported-schema="importedSchema!" :data-entries="dataEntries" @update:data-entry="emit('update:data-entry', $event)" />
                </template>
              </div>
            </div>
          </template>
          <!-- Si racine, afficher aussi les orphelins sans parent existant -->
          <template v-if="level === 0 && orphanGraftsByAnchor['__ROOT__'] && orphanGraftsByAnchor['__ROOT__'].length">
            <div class="mt-4">
              <div class="flex items-center mb-2">
                <InfoIcon class="w-4 h-4 text-yellow-500 mr-1" />
                <span class="text-sm text-yellow-700">Sections importées d'un modèle externe (non présentes dans ce schéma)</span>
              </div>
              <div>
                <template v-for="({ orphanPath: orphanPathOrphan, entry: entryOrphan }) in orphanGraftsByAnchor['__ROOT__']" :key="entryOrphan.name">
                  <OrphanGraftRenderer :orphan-path="orphanPathOrphan" :entry="entryOrphan" :imported-schema="importedSchema!" :data-entries="dataEntries" @update:data-entry="emit('update:data-entry', $event)" />
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </template>

<script setup lang="ts">
import { getDisplayType } from '@/types/compiledSections';
import type { SchemaJsonWithIds, SectionJsonWithId } from '@/utils/schemaExportUtils';
import { InfoIcon } from 'lucide-vue-next';
import { computed, defineComponent, h, ref, type VNode } from 'vue';
import type { MemoDataEntry } from '../api/types/memos/entries';

const props = defineProps<{
  compiledSectionData: SectionJsonWithId;
  dataEntries: MemoDataEntry[];
  level: number;
  compiledSchema: SchemaJsonWithIds;
  importedSchema?: SchemaJsonWithIds | null;
}>();

const emit = defineEmits<{
  'update:data-entry': [entry: MemoDataEntry]
}>();

// Computed properties pour le système de types de sections
const sectionDisplayType = computed(() => {
  const displayType = getDisplayType(props.compiledSectionData.type);

  // Debug pour les types dropdown et collapse
  if (displayType === 'dropdown' || displayType === 'collapse') {
    console.log(`🎯 FOUND ${displayType.toUpperCase()} SECTION: ${props.compiledSectionData.id}`);
  }

  return displayType;
});

// Récupérer le titre et la description à partir des traductions compilées
const sectionTitle = computed((): string => {
  const translations = props.compiledSectionData.translations;
  if (translations && translations.fr && translations.fr.titre) {
    return translations.fr.titre;
  }
  // Si pas de traduction, extraire la dernière partie du nom (ex: "WHICH.Instruments.Device" → "Device")
  const parts = props.compiledSectionData.id.split('.');
  return parts[parts.length - 1];
});

const sectionDescription = computed((): string => {
  const translations = props.compiledSectionData.translations;
  if (translations && translations.fr && translations.fr.description) {
    return translations.fr.description;
  }
  return '';
});

// Trouver les sections enfants dans le schéma compilé
const childSections = computed((): SectionJsonWithId[] => {
  const currentSectionId = props.compiledSectionData.id;
  const currentLevel = (props.compiledSectionData.indentationLevel || 0);

  const children = props.compiledSchema.section_list.filter(section => {
    // Un enfant direct a le niveau d'indentation currentLevel + 1
    // et son nom commence par currentSectionId + "."
    const expectedLevel = currentLevel + 1;
    const sectionLevel = section.indentationLevel || 0;

    return sectionLevel === expectedLevel &&
      section.id.startsWith(currentSectionId + '.');
  });

  return children;
});

// Vérifier si cette section fait partie d'un dropdown parent et doit être masquée au niveau racine
const shouldHideFromDropdownParent = computed((): boolean => {
  const currentSectionId = props.compiledSectionData.id;
  const currentLevel = (props.compiledSectionData.indentationLevel || 0);

  // Si on est au niveau 0, toujours afficher (sections racines)
  if (currentLevel === 0) return false;

  // NOUVELLE LOGIQUE : Masquer SEULEMENT si on est niveau 1 avec un parent dropdown niveau 0
  // ET qu'on est pas dans le contexte d'un composant récursif du dropdown (level > 1)
  if (currentLevel === 1) {
    const parentDropdown = props.compiledSchema.section_list.find(section => {
      const sectionLevel = section.indentationLevel || 0;
      return sectionLevel === 0 &&
        currentSectionId.startsWith(section.id + '.') &&
        getDisplayType(section.type) === 'dropdown';
    });

    if (parentDropdown) {
      console.log(`🚫 Section ${currentSectionId} masquée car enfant direct du dropdown parent ${parentDropdown.id}`);
      return true;
    }
  }

  // Pour les niveaux > 1, ne pas masquer (permettre récursion dans dropdown)
  return false;
});

// Computed: vérifier si des champs ont des valeurs (pour persistance)
const hasFilledFields = computed((): boolean => {
  if (!Array.isArray(props.dataEntries)) return false;

  // Chercher des entrées avec des valeurs non-vides pour cette section ou ses enfants
  const currentSectionId = props.compiledSectionData.id;

  return props.dataEntries.some((entry: MemoDataEntry) => {
    return entry.name && entry.name.startsWith(currentSectionId + '.') &&
      entry.value &&
      entry.value.trim() !== '';
  });
});

// Auto-expand si des champs sont remplis
const shouldAutoExpand = computed((): boolean => {
  return hasFilledFields.value;
});

// États pour la section collapse - auto-expand si des champs sont remplis
const isCollapsed = ref(!shouldAutoExpand.value);

// États pour la section dropdown
const selectedDropdownChildId = ref<number | ''>('');
const selectedDropdownChildIds = ref<Set<number>>(new Set());

// Auto-sélectionner les éléments du dropdown qui ont des valeurs remplies
const autoSelectFilledDropdownItems = (): void => {
  if (sectionDisplayType.value !== 'dropdown') return;

  // Trouver tous les enfants qui ont des valeurs remplies
  childSections.value.forEach(child => {
    if (hasFilledChildFields(child)) {
      selectedDropdownChildIds.value.add(child.section_id || 0);
    }
  });
};

// Vérifier si un enfant spécifique a des champs remplis
const hasFilledChildFields = (childSection: SectionJsonWithId): boolean => {
  if (!Array.isArray(props.dataEntries)) return false;

  return props.dataEntries.some((entry: MemoDataEntry) => {
    return entry.name &&
      (entry.name === childSection.id || (typeof childSection.id === 'string' && typeof entry.name === 'string' && entry.name.startsWith(childSection.id + '.'))) &&
      entry.value &&
      entry.value.trim() !== '';
  });
};

// Exécuter l'auto-sélection au montage
autoSelectFilledDropdownItems();

// Enfants disponibles pour le dropdown (non encore sélectionnés ET qui respectent leurs conditions)
const availableDropdownChildren = computed((): SectionJsonWithId[] => {
  return childSections.value.filter(child =>
    !selectedDropdownChildIds.value.has(child.section_id || 0) && shouldDisplaySection(child)
  );
});

// Enfants actuellement sélectionnés pour le dropdown
const selectedDropdownChildren = computed((): SectionJsonWithId[] => {
  return childSections.value.filter(child =>
    selectedDropdownChildIds.value.has(child.section_id || 0)
  );
});

const isFinalField = (sectionData: SectionJsonWithId): boolean => {
  // Les types 'collapse' et 'dropdown' sont des sections, pas des champs finaux
  if (sectionData.type && typeof sectionData.type === 'string') {
    const type = sectionData.type.toLowerCase().trim();
    if (type === 'collapse' || type === 'dropdown') {
      return false;
    }
  }

  // Si pas de type défini ou type vide, vérifier s'il y a des enfants dans le schéma global
  const hasChildren = props.compiledSchema.section_list.some(section =>
    typeof section.id === 'string' && typeof sectionData.id === 'string' && section.id.startsWith(sectionData.id + '.') && section.id !== sectionData.id
  );

  // Si pas d'enfants, c'est un champ final (comme Outils)
  return !hasChildren;
};

const shouldDisplayField = (sectionData: SectionJsonWithId): boolean => {
  // Vérifier d'abord si c'est un champ final et s'il est visible
  if (!isFinalField(sectionData) || sectionData.display === false) {
    return false;
  }

  // Si pas de conditions, afficher le champ
  if (!sectionData.conditions || sectionData.conditions.length === 0) {
    return true;
  }

  // Vérifier toutes les conditions (toutes doivent être satisfaites)
  return sectionData.conditions.every(condition => {
    const conditionId = condition.condition_id;
    const conditionValue = condition.condition_value;

    // Trouver la valeur actuelle du champ condition dans les données
    const conditionEntry = props.dataEntries.find(entry =>
      entry.name === conditionId
    );

    const currentValue = conditionEntry?.value || '';

    return checkCondition(currentValue, conditionValue);
  });
};

// Fonction utilitaire pour vérifier une condition spécifique
const checkCondition = (currentValue: string, conditionValue: string): boolean => {
  const stringValue = String(currentValue || '');

  switch (conditionValue) {
    case 'not_null':
    case 'not_empty':
      return Boolean(stringValue && stringValue.trim() !== '');
    case 'null':
    case 'empty':
      return Boolean(!stringValue || stringValue.trim() === '');
    case 'true':
      return stringValue === 'true';
    case 'false':
      return stringValue === 'false';
    default:
      // Comparaison exacte pour les autres valeurs
      return stringValue === conditionValue;
  }
};

const getFieldTitle = (sectionData: SectionJsonWithId): string => {
  const translations = sectionData.translations;
  if (translations && translations.fr && translations.fr.titre) {
    return translations.fr.titre;
  }
  // Si pas de traduction, extraire la dernière partie du nom
  const parts = sectionData.id.split('.');
  return parts[parts.length - 1];
};

const getFieldDescription = (sectionData: SectionJsonWithId): string => {
  const translations = sectionData.translations;
  if (translations && translations.fr && translations.fr.description) {
    return translations.fr.description;
  }
  return '';
};

const getFieldValue = (sectionData: SectionJsonWithId): string => {
  if (!Array.isArray(props.dataEntries)) {
    console.error("dataEntries n'est pas un tableau");
    return '';
  }

  const entry = props.dataEntries.find(
    (e: MemoDataEntry) => e.section_id === sectionData.section_id && e.name === sectionData.id
  );
  return entry?.value || '';
};

const handleInputChange = (sectionData: SectionJsonWithId, event: Event): void => {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
  if (!target) {
    console.error("Événement invalide dans handleInputChange");
    return;
  }

  const value = target.value;

  emit('update:data-entry', {
    section_id: sectionData.section_id || 0,
    value: value,
    name: sectionData.id
  });
};

// FOURTH_EDIT: ajout de handleBoolClick pour gérer les boutons de choix boolean
const handleBoolClick = (sectionData: SectionJsonWithId, value: string): void => {
  emit('update:data-entry', {
    section_id: sectionData.section_id || 0,
    value: value,
    name: sectionData.id
  });
};

// Fonctions pour la section collapse
const toggleCollapse = (): void => {
  isCollapsed.value = !isCollapsed.value;
};

// Fonctions pour la section dropdown
const handleDropdownSelection = (): void => {
  if (selectedDropdownChildId.value && typeof selectedDropdownChildId.value === 'number') {
    selectedDropdownChildIds.value.add(selectedDropdownChildId.value);
    selectedDropdownChildId.value = '';
  }
};

const removeDropdownChild = (childId: number): void => {
  selectedDropdownChildIds.value.delete(childId);

  // Supprimer les entrées de données associées
  const childToRemove = childSections.value.find(child =>
    (child.section_id || 0) === childId
  );
  if (childToRemove) {
    // Émettre un signal de suppression (avec valeur vide)
    emit('update:data-entry', {
      section_id: childToRemove.section_id || 0,
      value: '',
      name: childToRemove.id
    });
  }
};

// Vérifier si une section (finale ou non) doit être affichée selon ses conditions
const shouldDisplaySection = (sectionData: SectionJsonWithId): boolean => {
  // Vérifier d'abord si la section est visible
  if (sectionData.display === false) {
    return false;
  }

  // Si pas de conditions, afficher la section
  if (!sectionData.conditions || sectionData.conditions.length === 0) {
    return true;
  }

  // Vérifier toutes les conditions (toutes doivent être satisfaites)
  const conditionsMet = sectionData.conditions.every(condition => {
    const conditionId = condition.condition_id;
    const conditionValue = condition.condition_value;

    // Trouver la valeur actuelle du champ condition dans les données
    const conditionEntry = props.dataEntries.find(entry =>
      entry.name === conditionId
    );

    const currentValue = conditionEntry?.value || '';

    return checkCondition(currentValue, conditionValue);
  });

  return conditionsMet;
};

// --- NOUVEAU : Gestion des branches orphelines greffées dynamiquement ---

function getOrphanGraft(entryName: string | undefined): { anchor: string; orphanPath: string[] } {
  if (!props.importedSchema || !entryName) return { anchor: '__ROOT__', orphanPath: [] as string[] };
  const parts = String(entryName).split('.');
  let anchorIdx = -1;
  for (let i = parts.length - 2; i >= 0; i--) {
    const candidate = parts.slice(0, i + 1).join('.');
    if (props.compiledSchema.section_list.some(s => typeof s.id === 'string' && s.id === candidate)) {
      anchorIdx = i;
      break;
    }
  }
  return {
    anchor: anchorIdx >= 0 ? parts.slice(0, anchorIdx + 1).join('.') : '__ROOT__',
    orphanPath: parts.slice(anchorIdx + 1) as string[],
  };
}

// Regrouper les branches orphelines par parent d'ancrage
const orphanGraftsByAnchor = computed<Record<string, { orphanPath: string[]; entry: MemoDataEntry }[]>>(() => {
  if (!props.importedSchema) return {};
  const schemaFieldNames = new Set((props.compiledSchema.section_list.map(section => section.id)).filter((id): id is string => typeof id === 'string'));
  const externals = props.dataEntries.filter(entry => typeof entry.name === 'string' && !schemaFieldNames.has(entry.name));
  const result: Record<string, { orphanPath: string[]; entry: MemoDataEntry }[]> = {};
  for (const entry of externals) {
    const graft = getOrphanGraft(entry.name);
    if (!result[graft.anchor]) result[graft.anchor] = [];
    result[graft.anchor].push({ orphanPath: graft.orphanPath as string[], entry });
  }
  return result;
});

/*
// Utilitaire pour retrouver la meta d'une section dans le schéma importé
function getImportedSectionMeta(path: string[]): SectionJsonWithId | null {
  if (!props.importedSchema || !path.length) return null;
  let currentId = '';
  for (let i = 0; i < path.length; i++) {
    currentId = i === 0 ? path[0] : currentId + '.' + path[i];
  }
  return props.importedSchema.section_list.find(s => typeof s.id === 'string' && s.id === currentId) || null;
}*/

// Composant pour afficher dynamiquement la branche orpheline greffée
const OrphanGraftRenderer = defineComponent({
  name: 'OrphanGraftRenderer',
  props: {
    orphanPath: { type: Array as () => string[], required: true },
    entry: { type: Object as () => MemoDataEntry, required: true },
    importedSchema: { type: Object as () => SchemaJsonWithIds, required: true },
    dataEntries: { type: Array as () => MemoDataEntry[], required: true },
  },
  emits: ['update:data-entry'],
  setup(props, { emit }) {
    function getMeta(path: string[]): SectionJsonWithId | null {
      if (!props.importedSchema || !path.length) return null;
      let currentId = '';
      for (let i = 0; i < path.length; i++) {
        currentId = i === 0 ? path[0] : currentId + '.' + path[i];
      }
      return props.importedSchema.section_list.find(s => typeof s.id === 'string' && s.id === currentId) || null;
    }
    function getSectionTitle(meta: SectionJsonWithId | null, fallback: string): string {
      return (meta && 'appellation' in meta && typeof meta.appellation === 'string' && meta.appellation)
        || (meta && meta.translations?.fr?.titre)
        || fallback
        || '';
    }
    function getSectionDesc(meta: SectionJsonWithId | null): string | undefined {
      return (meta && 'description' in meta && typeof meta.description === 'string' && meta.description)
        || (meta && meta.translations?.fr?.description)
        || undefined;
    }
    // Affichage récursif de la branche orpheline, SANS AUCUNE CONDITION
    function renderOrphanBranch(path: string[], entry: MemoDataEntry, level = 0): VNode | null {
      // Log détaillé à chaque rendu de branche orpheline
      // eslint-disable-next-line no-console
      const sectionMeta = getMeta(path.slice(0, path.length - 1));
      const fieldMeta = getMeta(path);
      console.log('[ORPHAN_RENDER]', { path, entry, fieldMeta });
      if (!fieldMeta) {
        // Affichage minimal si meta absente
        return h('div', { class: 'pl-8 border-l-4 border-yellow-400 bg-yellow-50 rounded mb-2 p-2' }, [
          h('div', { class: 'flex items-center gap-2 mb-1' }, [
            h('span', { class: 'font-semibold text-yellow-900' }, path.join('.')),
            h('span', { class: 'text-xs text-yellow-700 ml-2 px-2 py-0.5 bg-yellow-200 rounded' }, 'Importé (meta absente)'),
          ]),
          h('input', {
            class: 'anamnesis-form-input bg-yellow-100 border-yellow-400',
            value: entry.value || '',
            onInput: (e: Event) => emit('update:data-entry', { ...entry, value: (e.target as HTMLInputElement).value }),
          }),
        ]);
      }
      if (path.length === 1) {
        // Feuille : champ orphelin
        return h('div', { class: 'pl-8 border-l-4 border-yellow-400 bg-yellow-50 rounded mb-2 p-2' }, [
          h('div', { class: 'flex items-center gap-2 mb-1' }, [
            h('span', { class: 'font-semibold text-yellow-900' }, getSectionTitle(fieldMeta, path[0] || '')),
            h('span', { class: 'text-xs text-yellow-700 ml-2 px-2 py-0.5 bg-yellow-200 rounded' }, 'Importé'),
          ]),
          getSectionDesc(fieldMeta) ? h('div', { class: 'text-xs text-yellow-700 mb-1' }, getSectionDesc(fieldMeta)) : null,
          h('input', {
            class: 'anamnesis-form-input bg-yellow-100 border-yellow-400',
            value: entry.value || '',
            onInput: (e: Event) => emit('update:data-entry', { ...entry, value: (e.target as HTMLInputElement).value }),
          }),
        ]);
      } else {
        // Section intermédiaire orpheline
        return h('div', { class: 'pl-4 border-l-4 border-yellow-100' }, [
          h('div', { class: 'flex items-center gap-2 mb-1' }, [
            h('span', { class: 'font-semibold text-yellow-900' }, getSectionTitle(sectionMeta, path[path.length - 2] || '')),
          ]),
          getSectionDesc(sectionMeta) ? h('div', { class: 'text-xs text-yellow-700 mb-1' }, getSectionDesc(sectionMeta)) : null,
          renderOrphanBranch(path.slice(1), entry, level + 1),
        ]);
      }
    }
    return () => renderOrphanBranch(props.orphanPath as string[], props.entry);
  },
});
</script>

<style scoped>
/* Styles pour la section collapse */
.collapse-section {
  margin-bottom: 1rem;
}

.collapse-header {
  user-select: none;
}

.collapse-arrow {
  transition: transform 0.2s ease;
  transform-origin: center;
}

.collapse-content {
  max-height: 2000px;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: 1;
}

.collapse-content.collapsed {
  max-height: 0;
  opacity: 0;
}

.collapse-content-inner {
  padding-bottom: 0.5rem;
}

/* Styles pour la section dropdown */
.dropdown-section {
  margin-bottom: 1.5rem;
}

.dropdown-header {
  margin-bottom: 1rem;
}

.dropdown-selector {
  margin-bottom: 1rem;
}

.selected-subsections {
  margin-top: 1rem;
}

.selected-subsection-item {
  position: relative;
  transition: all 0.2s ease;
}

.selected-subsection-item:hover {
  background-color: #f8fafc;
}

.selected-subsection-item {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .collapse-content-inner {
    padding-left: 1rem;
  }
}

/* Respect des préférences de mouvement réduit */
@media (prefers-reduced-motion: reduce) {

  .collapse-arrow,
  .collapse-content {
    transition: none;
  }
}
</style>
